import { useState, useEffect, useRef, useCallback } from 'react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink, Download, Loader2, AlertCircle, Maximize2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import ReportHtmlFrame from '@/components/ReportHtmlFrame';
import {
  getMasterAgentApiBase,
  isInlineHtml,
  isLikelySpaShellHtml,
  resolveReportFetchUrl,
} from '@/lib/reportHtml';
import { usePermissions } from '@/contexts/PermissionsContext';
import { fetchTicketSummaries } from '@/lib/ticketsApi';

const API = INCIDENTS_API;

export default function Reports() {
  const { hasPermission } = usePermissions();
  const canReadTickets = hasPermission('tickets', 'read');
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReportId, setActiveReportId] = useState(null);
  const [reportContent, setReportContent] = useState({});
  const [loadingContent, setLoadingContent] = useState({});
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const hasProcessedTicketId = useRef(false);

  const loadReportContent = useCallback(async (reportUrl, reportId) => {
    setLoadingContent(prev => {
      if (prev[reportId]) {
        // Already loading
        return prev;
      }
      return { ...prev, [reportId]: true };
    });
    setErrors(prev => ({ ...prev, [reportId]: null }));

    try {
      if (isInlineHtml(reportUrl)) {
        setReportContent((prev) => ({
          ...prev,
          [reportId]: reportUrl.trim(),
        }));
        return;
      }

      const fetchUrl = resolveReportFetchUrl(reportUrl) || reportUrl;
      if (!getMasterAgentApiBase() && (reportUrl.includes('/reports/') || fetchUrl.includes('/reports/'))) {
        throw new Error(
          'REACT_APP_VIBE_CODING_STUDIO_URL is not set. Reports must be loaded from the master agent API (…/api/reports/…), not the CloudFront SPA URL stored on the ticket.'
        );
      }

      const response = await axios.get(fetchUrl, {
        responseType: 'text',
        headers: {
          Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        },
        timeout: 30000,
        validateStatus: (status) => status < 500,
      });

      if (response.status === 200) {
        if (isLikelySpaShellHtml(response.data)) {
          throw new Error(
            'Received the Halo web app instead of report HTML. The stored link likely points at CloudFront; redeploy the master agent so tickets store …/api/reports/{file}.html, or ensure REACT_APP_VIBE_CODING_STUDIO_URL is set in the frontend build.'
          );
        }
        setReportContent((prev) => {
          if (prev[reportId]) {
            return prev;
          }
          return {
            ...prev,
            [reportId]: response.data,
          };
        });
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error(`Error loading report content for ${reportId}:`, error);
      
      let errorMessage = 'Failed to load report content. ';
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        errorMessage += 'The report server is not accessible. This may be a localhost URL that is not available. ';
      } else if (error.response?.status === 404) {
        errorMessage += 'Report file not found. The link may be expired or invalid. ';
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage += 'Request timed out. The report server may be slow or unavailable. ';
      }
      errorMessage += 'You can try opening the report in a new tab using the "Open in New Tab" button.';
      
      setErrors(prev => ({
        ...prev,
        [reportId]: errorMessage
      }));
    } finally {
      setLoadingContent(prev => ({ ...prev, [reportId]: false }));
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [canReadTickets]);

  // Handle ticketId query parameter after reports are loaded
  useEffect(() => {
    const ticketIdParam = searchParams.get('ticketId');
    
    if (ticketIdParam && reports.length > 0 && !loading && !hasProcessedTicketId.current) {
      // Find the report with matching ticket ID
      const matchingReport = reports.find(r => String(r.ticket.id) === String(ticketIdParam));
      if (matchingReport) {
        hasProcessedTicketId.current = true;
        setActiveReportId(matchingReport.ticket.id);
        // Open the report in modal view
        setSelectedReport(matchingReport);
        setIsModalOpen(true);
        // Load content if not already loaded
        if (!reportContent[matchingReport.ticket.id]) {
          loadReportContent(matchingReport.reportUrl, matchingReport.ticket.id);
        }
        // Clear the query parameter from URL
        setSearchParams({});
      }
    } else if (reports.length > 0 && !activeReportId && !ticketIdParam && !loading) {
      // Set first report as active if no ticketId param and no active report
      setActiveReportId(reports[0].ticket.id);
    }
  }, [reports, loading, searchParams, reportContent, activeReportId, setSearchParams, loadReportContent]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // Report list is derived from tickets. Without tickets.read, skip the call
      // so create/read-only insight roles are not logged out by a 401.
      if (!canReadTickets) {
        setReports([]);
        return;
      }
      const page = await fetchTicketSummaries(API, { limit: 50, offset: 0, includeTotal: false });
      
      // Filter tickets that are Static Report type
      const staticReportTickets = page.items.filter(
        ticket => ticket.customization_type?.toLowerCase() === 'static report' ||
                  ticket.category?.toLowerCase() === 'static report'
      );

      // Fetch code generations for each static report ticket
      const reportsWithCode = await Promise.all(
        staticReportTickets.map(async (ticket) => {
          try {
            const codeRes = await axios.get(`${API}/code/${ticket.ticket_number}`);
            const activeCode = codeRes.data.find(c => c.is_active) || codeRes.data[0];
            
            if (activeCode && activeCode.code) {
              return {
                ticket,
                code: activeCode,
                reportUrl: activeCode.code
              };
            }
            return null;
          } catch (err) {
            console.error(`Error fetching code for ticket ${ticket.ticket_number}:`, err);
            return null;
          }
        })
      );

      const validReports = reportsWithCode.filter(r => r !== null);
      setReports(validReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReportSelect = (reportId, reportUrl) => {
    setActiveReportId(reportId);
    if (!reportContent[reportId]) {
      loadReportContent(reportUrl, reportId);
    }
  };

  const handleReportClick = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
    // Load content if not already loaded
    if (!reportContent[report.ticket.id]) {
      loadReportContent(report.reportUrl, report.ticket.id);
    }
  };

  const openReportInNewTab = (reportId, reportUrl) => {
    const html = reportContent[reportId];
    if (html) {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
      return;
    }
    const fetchUrl = resolveReportFetchUrl(reportUrl) || reportUrl;
    window.open(fetchUrl, '_blank', 'noopener,noreferrer');
  };

  const downloadReport = (reportId, reportUrl, ticketNumber) => {
    const html = reportContent[reportId];
    if (html) {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${ticketNumber}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }
    const link = document.createElement('a');
    link.href = resolveReportFetchUrl(reportUrl) || reportUrl;
    link.download = `report-${ticketNumber}.html`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cp-indigo" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground mt-2">View and manage static HTML reports</p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            {!canReadTickets ? (
              <>
                <p className="text-muted-foreground mb-4">Ticket reports need tickets.read</p>
                <p className="text-sm text-muted-foreground">
                  Your role can open Reports, but the ticket-backed report list is hidden until that scope is granted.
                </p>
              </>
            ) : (
              <>
                <p className="text-muted-foreground mb-4">No static reports available yet</p>
                <p className="text-sm text-muted-foreground">Static reports will appear here once generated for tickets</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeReport = reports.find(r => r.ticket.id === activeReportId) || reports[0];
  const currentContent = reportContent[activeReportId];
  const isLoading = loadingContent[activeReportId];
  const error = errors[activeReportId];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-2">View and manage static HTML reports</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Reports List */}
        <div className="col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Available Reports</CardTitle>
              <CardDescription>
                {reports.length} report{reports.length !== 1 ? 's' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {reports.map((report) => {
                  const isActive = report.ticket.id === activeReportId;
                  return (
                    <button
                      key={report.ticket.id}
                      onClick={() => handleReportClick(report)}
                      className={`
                        w-full text-left p-4 hover:bg-cp-inset transition-colors
                        ${isActive ? 'bg-cp-inset border-l-4 border-cp-indigo' : ''}
                      `}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="font-mono text-xs">
                              {report.ticket.ticket_number}
                            </Badge>
                            {isActive && (
                              <Badge className="bg-cp-indigo text-white text-xs">Active</Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium text-cp-fg truncate">
                            {report.ticket.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(report.code.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Report Viewer */}
        <div className="col-span-9">
          <Card className="h-[calc(100vh-12rem)] flex flex-col">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {activeReport.ticket.title}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Ticket: {activeReport.ticket.ticket_number} • 
                    Generated: {new Date(activeReport.code.created_at).toLocaleString()}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleReportClick(activeReport)}
                  >
                    <Maximize2 className="h-4 w-4 mr-2" />
                    View Full Screen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openReportInNewTab(activeReportId, activeReport.reportUrl)}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in New Tab
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      downloadReport(activeReportId, activeReport.reportUrl, activeReport.ticket.ticket_number)
                    }
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-cp-indigo mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading report content...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="p-6">
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-2">Report URL:</p>
                    <a
                      href={activeReport.reportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cp-indigo hover:text-cp-muted break-all text-sm"
                    >
                      {activeReport.reportUrl}
                    </a>
                  </div>
                </div>
              ) : currentContent ? (
                <ReportHtmlFrame
                  html={currentContent}
                  title={`Report ${activeReport.ticket.ticket_number}`}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">Click on a report to view it</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Full-Width Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] p-0 flex flex-col !left-[2.5vw] !top-[2.5vh] !translate-x-0 !translate-y-0">
          {selectedReport && (
            <>
              <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <DialogTitle className="flex items-center gap-2 text-xl">
                      <FileText className="h-5 w-5" />
                      {selectedReport.ticket.title}
                    </DialogTitle>
                    <DialogDescription className="mt-2">
                      Ticket: {selectedReport.ticket.ticket_number} • 
                      Generated: {new Date(selectedReport.code.created_at).toLocaleString()}
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openReportInNewTab(selectedReport.ticket.id, selectedReport.reportUrl)}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open in New Tab
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        downloadReport(
                          selectedReport.ticket.id,
                          selectedReport.reportUrl,
                          selectedReport.ticket.ticket_number
                        )
                      }
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-hidden p-0">
                {loadingContent[selectedReport.ticket.id] ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-cp-indigo mx-auto mb-4" />
                      <p className="text-muted-foreground">Loading report content...</p>
                    </div>
                  </div>
                ) : errors[selectedReport.ticket.id] ? (
                  <div className="p-6">
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{errors[selectedReport.ticket.id]}</AlertDescription>
                    </Alert>
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-2">Report URL:</p>
                      <a
                        href={selectedReport.reportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cp-indigo hover:text-cp-muted break-all text-sm"
                      >
                        {selectedReport.reportUrl}
                      </a>
                    </div>
                  </div>
                ) : reportContent[selectedReport.ticket.id] ? (
                  <ReportHtmlFrame
                    html={reportContent[selectedReport.ticket.id]}
                    title={`Report ${selectedReport.ticket.ticket_number}`}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">Loading report...</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
